import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBillWave, faPlus } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { FINANCE_NAV } from './nav';

interface Payout { id: number; institution: string; period_start: string; period_end: string; amount: number; status: string; reference: string; paid_at: string | null; }
interface InstOption { id: number; name: string; }

const STATUS_COLORS: Record<string, string> = { pending: 'secondary', processing: 'info', paid: 'success', failed: 'danger', cancelled: 'dark' };

const Payouts: React.FC = () => {
    const { api } = useInternalApi();
    const [payouts, setPayouts] = useState<Payout[]>([]);
    const [partnerRowCount, setPartnerRowCount] = useState(0);
    const [institutions, setInstitutions] = useState<InstOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({ institution_id: '', period_start: '', period_end: '', amount: '', reference: '' });

    useEffect(() => { document.title = 'Payouts & Settlement - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get('admin-dashboard/finance/payouts/');
            if (res.data?.status === 'success') {
                setPayouts(res.data.data.payouts || []);
                setPartnerRowCount(res.data.data.partner_network_row_count || 0);
            }
        } finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        api.get('admin-dashboard/finance/institutions-lite/').then(res => {
            if (res.data?.status === 'success') setInstitutions(res.data.data || []);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitCreate = async () => {
        if (!form.institution_id || !form.period_start || !form.period_end || !form.amount) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Institution, period and amount are required.' });
            return;
        }
        try {
            const res = await api.post('admin-dashboard/finance/payouts/', form);
            if (res.data?.status === 'success') {
                setShowCreate(false);
                setForm({ institution_id: '', period_start: '', period_end: '', amount: '', reference: '' });
                load();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Could not create payout.' });
        }
    };

    const updateStatus = async (p: Payout, status: string) => {
        try {
            await api.patch(`admin-dashboard/finance/payouts/${p.id}/`, { status });
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not update payout.' });
        }
    };

    return (
        <InternalShell activeDashboard="finance_center" subNav={FINANCE_NAV}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faMoneyBillWave} className="me-2 text-primary" />Payouts & Settlement</h2>
                    <p className="text-muted mb-0">Institution payout runs</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}><FontAwesomeIcon icon={faPlus} className="me-2" />New Payout</button>
            </div>

            {partnerRowCount === 0 && (
                <div className="alert alert-secondary small">
                    The marketplace Partner network currently has no real accounts (0 rows) — that scope is tied to an open architectural decision (tracker item 1.12). Payouts here settle real <strong>Institutions</strong> instead, which is the platform's actual tenant/finance-recipient entity today.
                </div>
            )}

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : payouts.length === 0 ? (
                        <div className="text-center py-5 text-muted">No payouts recorded yet.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light"><tr><th>Institution</th><th>Period</th><th>Amount</th><th>Reference</th><th>Status</th><th>Action</th></tr></thead>
                            <tbody>
                                {payouts.map(p => (
                                    <tr key={p.id}>
                                        <td>{p.institution}</td>
                                        <td>{p.period_start} → {p.period_end}</td>
                                        <td>KES {p.amount.toLocaleString()}</td>
                                        <td>{p.reference || '—'}</td>
                                        <td><span className={`badge bg-${STATUS_COLORS[p.status]}`}>{p.status}</span></td>
                                        <td>
                                            {p.status === 'pending' && <button className="btn btn-sm btn-outline-info me-1" onClick={() => updateStatus(p, 'processing')}>Process</button>}
                                            {(p.status === 'pending' || p.status === 'processing') && <button className="btn btn-sm btn-outline-success" onClick={() => updateStatus(p, 'paid')}>Mark Paid</button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Payout</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Institution</label>
                                    <select className="form-select" value={form.institution_id} onChange={(e) => setForm(f => ({ ...f, institution_id: e.target.value }))}>
                                        <option value="">Select...</option>
                                        {institutions.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                                    </select>
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <label className="form-label">Period Start</label>
                                        <input type="date" className="form-control" value={form.period_start} onChange={(e) => setForm(f => ({ ...f, period_start: e.target.value }))} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Period End</label>
                                        <input type="date" className="form-control" value={form.period_end} onChange={(e) => setForm(f => ({ ...f, period_end: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Amount (KES)</label>
                                    <input type="number" className="form-control" value={form.amount} onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Reference</label>
                                    <input className="form-control" value={form.reference} onChange={(e) => setForm(f => ({ ...f, reference: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={submitCreate}>Create</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </InternalShell>
    );
};

export default Payouts;
